import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useActiveSection } from '@/hooks/useActiveSection';
import { useEffect, useState } from 'react';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeId = useActiveSection();
  const { ref, isVisible } = useScrollReveal();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinkBase = 'text-xs font-medium tracking-[0.2em] uppercase whitespace-nowrap transition-colors duration-300 cursor-pointer';

  const isActive = (id: string) => activeId === id;

  const navLinks = [
    { href: '#home', label: 'HOME' },
    { href: '#works', label: 'WORKS' },
    { href: '#achievements', label: 'ACHIEVEMENTS' },
    { href: '#profile', label: 'PROFILE' },
    { href: '#contact', label: 'CONTACT' },
  ];

  return (
    <nav
      ref={ref}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-warm-ivory/95 backdrop-blur-lg border-b border-champagne-200'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 md:px-8 flex items-center justify-between h-16 md:h-20">
        <a
          href="#home"
          onClick={(e) => handleNavClick(e, '#home')}
          className={`flex items-center gap-3 cursor-pointer whitespace-nowrap ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3'
          } transition-all duration-700 ease-out`}
        >
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-full overflow-hidden ring-1 ring-gold-300/50 shrink-0">
            <img
              alt="アメリ"
              className="w-full h-full object-cover object-top"
              src="https://storage.readdy-site.link/project_files/346f2ee0-d729-4568-8b32-aa7af4cc60d4/22ecbaa5-ec84-4a18-bb9f-79a69d1f1d2a_compressed_555802444_777730355090152_2918592743102479657_n.webp"
            />
          </div>
          <span className="text-sm md:text-base font-heading font-semibold tracking-wider text-charcoal-700 whitespace-nowrap">AMERI</span>
        </a>

        <div className="hidden md:flex items-center gap-10">
          {navLinks.map(({ href, label }) => (
            <a
              key={href}
              href={href}
              onClick={(e) => handleNavClick(e, href)}
              className={`${navLinkBase} ${
                isActive(href.replace('#', ''))
                  ? 'text-gold-500'
                  : 'text-charcoal-500 hover:text-gold-500'
              }`}
            >
              {label}
              <span
                className={`block h-px mt-1 bg-gold-400 transition-all duration-300 ${
                  isActive(href.replace('#', '')) ? 'w-full' : 'w-0'
                }`}
              ></span>
            </a>
          ))}
        </div>

        <button
          className="md:hidden w-9 h-9 flex items-center justify-center cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl text-charcoal-700 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-warm-ivory/98 backdrop-blur-lg border-t border-champagne-200">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navLinks.map(({ href, label }) => (
              <a
                key={href}
                href={href}
                onClick={(e) => handleNavClick(e, href)}
                className={`text-xs font-medium tracking-[0.2em] uppercase transition-colors duration-200 py-2 cursor-pointer ${
                  isActive(href.replace('#', ''))
                    ? 'text-gold-500'
                    : 'text-charcoal-500 hover:text-gold-500'
                }`}
              >
                {label}
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}